import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Form, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import {
  GET_STATUS_SALE_PLANNING,
  VARIABLES_GET_STATUS_SALE_PLANNING,
} from 'apis/GraphQL/Queries/codeQuery';
import { ISearchRouteComboBox } from 'apis/GraphQL/Queries/saleLineQuery';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import { SaleLineApi } from 'apis/SaleLineApi';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { FormFilter, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import { IFilterTeamSaleCb } from 'interfaces/TeamSale/TeamSale';
import Content from 'layouts/Content';
import { useEffect, useMemo, useState } from 'react';
import convertToLabelValueCbb from 'utils/convertToLabelValueCbb';

const spanCol = 8;

interface IFilterSalePlanning {
  isOpenFilter: boolean;
  filterData: any;
  isAdd: boolean;
  optionPlants: any;
  defaultFilter: any;
  onFilter(values: any): void;
  onCancel: () => void;
}

export default function FilterSalePlan({
  isOpenFilter,
  filterData,
  optionPlants,
  defaultFilter,
  onFilter,
  onCancel,
}: IFilterSalePlanning) {
  const { user } = useUser();

  const [form] = useForm();

  const [teamSaleData, setTeamSaleData] =
    useState<ILazySelectData>(initLazySelectData);
  const [fromDate, setFromDate] = useState<any>();
  const [toDate, setToDate] = useState<any>();
  const [teamCapacity] = useState<any>([
    {
      label: 'Đủ Năng Lực',
      value: 'Đủ Năng Lực',
    },
    {
      label: 'Dư Năng Lực',
      value: 'Dư Năng Lực',
    },
    {
      label: 'Thiếu Năng Lực',
      value: 'Thiếu Năng Lực',
    },
  ]);
  const [routes, setRoutes] = useState<{
    items: any;
    pageInfo: any;
  }>({
    items: [],
    pageInfo: {},
  });
  const [statuses, setStatuses] = useState<any[]>([]);
  const teamOptions = useMemo(
    () =>
      ((user as any)?.teamSales ?? []).map((item: any) => ({
        label: item.subId + ' - ' + item.name,
        value: item.subId,
      })) ?? [],
    [user],
  );

  const statusParams = {
    query: GET_STATUS_SALE_PLANNING,
    variables: VARIABLES_GET_STATUS_SALE_PLANNING('107'),
  };

  const { isPending: getRouteLoading, mutate: getRouteCb } = useMutation({
    mutationFn: (params: ISearchRouteComboBox) =>
      SaleLineApi.getAllRouteComboBox(params),
    onSuccess: (res) => {
      const items = res.data?.getRouteM?.items || [];

      setRoutes({
        items: convertToLabelValueCbb(items),
        pageInfo: res.data?.getRouteM.pageInfo,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { isPending: statusPending, mutate: getAllStatuses } = useMutation({
    mutationFn: () => axiosGraphQL.post('/graphql', statusParams),
    onSuccess: (data: any) => {
      const items = data.data?.getCode?.items || [];

      setStatuses(
        items.map((item: any) => ({
          label: item.name,
          value: item.id,
        })),
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });
  const { isPending: getTeamSaleLoading, mutate: getTeamSales } = useMutation({
    mutationFn: (params: IFilterTeamSaleCb) =>
      SaleTeamApi.getAllTeamSaleCbSubSort(params),
    onSuccess: (res) => {
      if (res?.data?.getTeamSales) {
        const items = res.data?.getTeamSales?.items ?? [],
          totalItems = res.data?.getTeamSales?.pageInfo?.totalItems ?? 0;

        setTeamSaleData({
          options: items.map((item: any) => ({
            label: item.subId + ' - ' + item.name,
            value: `${item.id}.${item.subId}.${item.plantId}`,
          })),
          totalItems: totalItems,
        });
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const onFinish = async (values: any) => {
    const [, teamSubId, plantId] = (values.teamSalesId || '')?.split('.');

    onFilter({
      ...values,
      ...(fromDate && {
        fromDate: fromDate.format('YYYY-MM-DD 00:00:00'),
      }),
      ...(toDate && { toDate: toDate.format('YYYY-MM-DD 00:00:00') }),
      teamSalesId: teamSubId || values.teamSalesId,
      plantId: plantId || values.plantId,
    });
  };

  const handleCancel = () => {
    form.resetFields();
    form.setFieldsValue({
      ...defaultFilter,
      teamSalesId: defaultFilter?.teamSalesSubId,
    });

    setFromDate(undefined);
    setToDate(undefined);
    onCancel();
  };

  const handleRouteSelectScrollChange = (params: ILazySelectParams) => {
    const plant = form.getFieldValue('plantId');
    getRouteCb({
      value: params.value,
      plantId: plant || defaultFilter.plantId,
      pageSize: params.pageSize,
    });
  };

  const handleTeamSaleScrollChange = (params: ILazySelectParams) => {
    const plant = form.getFieldValue('plantId');
    const teamSaleParams: IFilterTeamSaleCb = {
      ...params,
      plantId: plant || defaultFilter.plantId,
    };

    getTeamSales(teamSaleParams);
  };

  const handleChangePlant = (value: any) => {
    form.setFieldsValue({
      teamSalesId: undefined,
      routeId: undefined,
      plantId: value,
    });

    handleTeamSaleScrollChange({
      pageSize: 20,
      value: '',
    });

    handleRouteSelectScrollChange({
      pageSize: 20,
      value: '',
    });
  };

  useEffect(() => {
    form.setFieldsValue({
      ...defaultFilter,
      teamSalesId: defaultFilter?.teamSalesSubId,
    });
  }, [defaultFilter, form, user]);

  useEffect(() => {
    getAllStatuses();
  }, [getAllStatuses]);

  useEffect(() => {
    if (filterData?.fromDate) {
      const fromDate = dayjs(filterData.fromDate);
      form.setFieldValue('fromDate', fromDate);
      setFromDate(fromDate);
    }

    if (filterData?.toDate) {
      const toDate = dayjs(filterData.toDate);
      form.setFieldValue('toDate', toDate);
      setToDate(toDate);
    }
  }, [filterData.fromDate, filterData.toDate, form]);

  useEffect(() => {
    if (!isOpenFilter) return;

    const fromDate = filterData?.fromDate
      ? dayjs(filterData.fromDate)
      : undefined;
    const toDate = filterData?.toDate ? dayjs(filterData.toDate) : undefined;

    form.setFieldsValue({
      ...defaultFilter,
      ...filterData,
      fromDate,
      toDate,
      teamSalesId: defaultFilter?.teamSalesSubId,
    });
    setFromDate(fromDate);
    setToDate(toDate);
  }, [isOpenFilter]);

  return (
    <Content>
      <FormFilter form={form} onFinish={onFinish} onCancel={handleCancel}>
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Chi nhánh" name="plantId">
              <SelectForm
                disabled={user?.plantCode !== '5000'}
                className="text-red"
                placeholder="Chọn chi nhánh"
                options={optionPlants?.items || []}
                fieldNames={{
                  label: 'value',
                }}
                onChange={handleChangePlant}
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Tuyến bán hàng" name="routeId">
              {/* <SelectForm
                loading={getRouteLoading}
                placeholder="Chọn tuyến"
                options={routes.items}
              /> */}
              <LazySelectForm
                customeSize={20}
                data={{
                  options: routes.items || [],
                  totalItems: routes.pageInfo?.totalItems || 0,
                }}
                loading={getRouteLoading}
                placeholder="Chọn tuyến"
                onSelectScrollChange={handleRouteSelectScrollChange}
              />
            </Form.Item>
          </Col>

          <Col span={spanCol} className="h-[80px]">
            <Form.Item
              label="Đội bán hàng"
              name="teamSalesId"
              initialValue={defaultFilter?.teamSalesSubId}
            >
              {user?.isSale ? (
                <SelectForm
                  disabled
                  placeholder="Chọn đội"
                  options={teamOptions}
                />
              ) : (
                <LazySelectForm
                  customeSize={20}
                  data={teamSaleData}
                  placeholder="Chọn đội"
                  loading={getTeamSaleLoading}
                  onSelectScrollChange={handleTeamSaleScrollChange}
                />
              )}
            </Form.Item>
          </Col>

          <Col span={6} className="h-[80px]">
            <Form.Item label="Từ ngày" name="fromDate">
              <DatePicker
                maxDate={toDate}
                format={'DD/MM/YYYY'}
                onChange={setFromDate}
                style={{ width: '100%' }}
                size="large"
                placeholder="Từ ngày"
              />
            </Form.Item>
          </Col>

          <Col span={6} className="h-[80px]">
            <Form.Item label="Đến ngày" name="toDate">
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                size="large"
                minDate={fromDate}
                onChange={setToDate}
                placeholder="Đến ngày"
              />
            </Form.Item>
          </Col>

          <Col span={6} className="h-[80px]">
            <Form.Item label="Trạng thái" name="statusId">
              <SelectForm
                loading={statusPending}
                placeholder="Chọn trạng thái"
                options={statuses}
              />
            </Form.Item>
          </Col>
          <Col span={6} className="h-[80px]">
            <Form.Item label="Năng lực" name="teamCapacity">
              <SelectForm placeholder="Chọn năng lực" options={teamCapacity} />
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
}
